import React from "react";

export interface UseOpenLeaseInput {
  /** A disabled list never opens. */
  readonly disabled?: boolean | undefined;
  /** Fires when the list opens. */
  readonly onFocus?: (() => void) | undefined;
  /** Fires when an open list closes, by any route. */
  readonly onBlur?: (() => void) | undefined;
}

export interface OpenLease {
  readonly open: boolean;
  readonly openList: () => void;
  readonly closeList: () => void;
}

/**
 * A person list's OPEN lifetime, with the PB-4923 edit lease bound to it.
 *
 * A caller leasing "somebody is choosing here" cannot use an input's own
 * focus events: the popover owns focus once the list is open, and it never
 * blurs at all on the outside-press path — `useDismissable` closes the popover
 * while focus stays put. So `onFocus` fires when the list opens and `onBlur`
 * when it closes, by any route (commit, Escape, outside press). Only a close
 * that follows an open is reported: Escape and an outside press both reach
 * `closeList` on a shut list, and a release the caller never claimed would
 * drop somebody else's lease on a shared target.
 */
export function useOpenLease({
  disabled = false,
  onFocus,
  onBlur,
}: UseOpenLeaseInput): OpenLease {
  const [open, setOpen] = React.useState(false);
  const openRef = React.useRef(false);
  const openList = (): void => {
    if (disabled || openRef.current) return;
    openRef.current = true;
    setOpen(true);
    onFocus?.();
  };
  const closeList = (): void => {
    const wasOpen = openRef.current;
    openRef.current = false;
    if (wasOpen) onBlur?.();
    setOpen(false);
  };
  return { open, openList, closeList };
}
